import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { PartyPopper, ThumbsUp, Trophy, TriangleAlert } from "lucide-react";
import CommonTable from "../../common_components/CommonTable";
import { EXPAND_TRIGGERS } from "../../common_components/tableConstants";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import { TooltipRows } from "./EmployeePerformance";
const FONT_FAMILY = "Inter, sans-serif";
/** One scale drives both the Bonus Summary message and the AM header status pill. */
export const bonusLevelFor = (totalBonus) => totalBonus > 100 ? "high" : totalBonus > 40 ? "mid" : "low";
const bonusMessageFor = (totalBonus) => {
    if (totalBonus > 100) {
        return {
            text: "Yay! You've exceeded your goals.", icon: _jsx(Trophy, { size: 16 }),
            bg: "#FDF2FA", border: "#FCCEEE", textColor: "#C11574", iconBg: "#FCE7F6", iconColor: "#D10075",
        };
    }
    if (totalBonus > 40) {
        return {
            text: "Great job! You've hit your goals", icon: _jsx(ThumbsUp, { size: 16 }),
            bg: "#ECFDF3", border: "#ABEFC6", textColor: "#067647", iconBg: "#DCFAE6", iconColor: "#079455",
        };
    }
    return {
        text: "Your accounts need more attention", icon: _jsx(TriangleAlert, { size: 16 }),
        bg: "#FFFAEB", border: "#FEDF89", textColor: "#B54708", iconBg: "#FEF0C7", iconColor: "#DC6803",
    };
};
const columns = [
    { id: "target", label: "Target", width: "25%", align: "left", sortable: true },
    { id: "bonus_per_goal", label: "Bonus per Hitting Goal.", width: "25%", align: "left" },
    { id: "accounts_beat", label: "No. Of Account Beat", width: "25%", align: "left" },
    { id: "total_bonus", label: "Total Bonus Value", width: "25%", align: "left" },
];
const formatMoneyValue = (n) => (n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
// Grey "$" with a small gap, same treatment as the rest of the module.
const Money = ({ value }) => (_jsxs(_Fragment, { children: [_jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), formatMoneyValue(value)] }));
export default function EmployeeBonusSummary({ bonus, noRecords = false }) {
    const [sortDirection, setSortDirection] = useState(null);
    const rows = useMemo(() => (bonus?.rows || []).map((r) => ({
        id: r.target_label,
        target: r.target_label,
        bonus_per_goal: r.bonus_amount,
        accounts_beat: r.accounts_beat,
        total_bonus: r.total_bonus,
    })), [bonus]);
    const totalBonus = bonus?.total_bonus ?? 0;
    // Header message by Total Bonus Value: <= 40, over 40 up to 100, over 100.
    const bonusMessage = bonusMessageFor(totalBonus);
    // Target is the only sortable column ("Beat By 10%" ... by the percentage).
    const sortedRows = useMemo(() => {
        if (!sortDirection)
            return rows;
        const pct = (r) => Number(r.target.replace(/\D/g, "")) || 0;
        const dir = sortDirection === "desc" ? -1 : 1;
        return [...rows].sort((a, b) => (pct(a) - pct(b)) * dir);
    }, [rows, sortDirection]);
    const renderCell = (row, col) => {
        switch (col.id) {
            case "target": return row.target;
            case "bonus_per_goal": return _jsx(Money, { value: row.bonus_per_goal });
            case "accounts_beat": {
                // Hover the count to see which clients landed in this tier (same tooltip as the listing).
                const clients = (bonus?.breakdown || []).filter((b) => b.target_label === row.target);
                if (!clients.length)
                    return row.accounts_beat;
                const hasBothReports = new Set((bonus?.breakdown || []).map((b) => b.report)).size > 1;
                return (_jsx(HoverTooltip, { title: _jsx(TooltipRows, { heading: "Clients", items: clients.map((c) => ({
                            name: c.name,
                            value: `${c.var_percent}%`,
                            sub: hasBothReports ? (c.report === "affiliate" ? "Affiliate VAR" : "Revenue VAR") : undefined,
                        })) }), children: _jsx("span", { style: { cursor: "pointer" }, children: row.accounts_beat }) }));
            }
            case "total_bonus": return _jsx(Money, { value: row.total_bonus });
            default: return null;
        }
    };
    return (_jsxs(Box, { sx: { bgcolor: "#fff", border: "1px solid #EAECF0", borderRadius: "8px", overflow: "hidden" }, children: [_jsxs(Box, { sx: {
                    px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 2, flexWrap: "wrap",
                }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Box, { sx: { display: "flex", color: "#181D27" }, children: _jsx(PartyPopper, { size: 22 }) }), _jsx(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "16px", fontWeight: 600, color: "#181D27" }, children: "Bonus Summary" })] }), !noRecords && _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }, children: [_jsxs(Box, { sx: {
                                    display: "inline-flex", alignItems: "center", gap: 1,
                                    bgcolor: bonusMessage.bg, border: `1px solid ${bonusMessage.border}`, borderRadius: "8px",
                                    pl: 0.75, pr: 1.5, py: 0.5,
                                }, children: [_jsx(Box, { sx: {
                                            width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            bgcolor: bonusMessage.iconBg, color: bonusMessage.iconColor,
                                        }, children: bonusMessage.icon }), _jsx(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "14px", fontWeight: 500, color: bonusMessage.textColor }, children: bonusMessage.text })] }), _jsxs(Box, { sx: {
                                    display: "flex", alignItems: "stretch", border: "1px solid #D0D5DD", borderRadius: "8px", overflow: "hidden",
                                }, children: [_jsx(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "14px", fontWeight: 500, color: "#181D27", px: 1.75, py: 0.75 }, children: "Total Bonus Value" }), _jsx(Typography, { sx: {
                                            fontFamily: FONT_FAMILY, fontSize: "14px", fontWeight: 500, color: "#181D27", px: 1.75, py: 0.75,
                                            borderLeft: "1px solid #D0D5DD", bgcolor: "#FAFAFA",
                                        }, children: _jsx(Money, { value: totalBonus }) })] })] })] }), noRecords ? (_jsx(Box, { sx: { py: 6, display: "flex", alignItems: "center", justifyContent: "center", borderTop: "1px solid #EAECF0" }, children: _jsx(Typography, { sx: { fontFamily: FONT_FAMILY, fontSize: "16px", fontWeight: 500, color: "#667085" }, children: "No records found." }) })) : (_jsx(CommonTable, { columns: columns, rows: sortedRows, renderCell: renderCell, isLoading: false, expandTrigger: EXPAND_TRIGGERS.NONE, sortConfig: sortDirection ? { columnId: "target", direction: sortDirection } : null, onSortChange: (columnId, direction) => {
                    if (columnId === "target")
                        setSortDirection(direction);
                }, emptyMessage: "No bonus data.", enableColumnActions: false, enableColumnReorder: false, enableColumnPinning: false, enableColumnFilters: false, enableColumnVisibility: false, enableSelection: false }))] }));
}
